// src/pages/driverReports/POSSummaryReport.js
import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const WEEKS = ["week1", "week2", "week3", "week4", "week5"];

const WEEK_COLORS = {
  week1: "#e3f2fd", // light blue
  week2: "#e8f5e9", // light green
  week3: "#fffde7", // light yellow
  week4: "#fff3e0", // light orange
  week5: "#f3e5f5", // light purple
};

// Helper: convert CSS hex to Excel ARGB
function cssHexToExcelARGB(cssHex) {
  const hex = cssHex.replace("#", "");
  return "FF" + hex.toUpperCase();
}

// Get week from date (same as other reports)
function getWeekKeyFromDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const day = Number(parts[2]);
  if (Number.isNaN(day)) return null;

  if (day <= 7) return "week1";
  if (day <= 14) return "week2";
  if (day <= 21) return "week3";
  if (day <= 28) return "week4";
  return "week5";
}

export default function POSSummaryReport() {
  const [month, setMonth] = useState("");
  const [weekFrom, setWeekFrom] = useState("week1");
  const [weekTo, setWeekTo] = useState("week5");
  const [company, setCompany] = useState(""); // Optional: filter by company

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [weekTotals, setWeekTotals] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [companies, setCompanies] = useState([]);

  // Load unique companies from vehicles
  useEffect(() => {
    async function loadCompanies() {
      const vehiclesSnap = await getDocs(collection(db, "vehicles"));
      const companySet = new Set();
      vehiclesSnap.forEach((doc) => {
        const data = doc.data();
        if (data.company) companySet.add(data.company);
      });
      setCompanies(Array.from(companySet).sort());
    }
    loadCompanies();
  }, []);

  async function generateReport() {
    if (!month) {
      alert("Please select a month.");
      return;
    }

    const fromIndex = WEEKS.indexOf(weekFrom);
    const toIndex = WEEKS.indexOf(weekTo);
    if (fromIndex === -1 || toIndex === -1 || fromIndex > toIndex) {
      alert("Invalid week range.");
      return;
    }

    const selectedWeeks = WEEKS.slice(fromIndex, toIndex + 1);

    // Build date range
    const [yearStr, monthStr] = month.split("-");
    const year = Number(yearStr);
    const monthNum = Number(monthStr);
    if (!year || !monthNum) {
      alert("Invalid month format.");
      return;
    }
    const lastDay = new Date(year, monthNum, 0).getDate();
    const startDateStr = `${month}-01`;
    const endDateStr = `${month}-${String(lastDay).padStart(2, "0")}`;

    setLoading(true);
    try {
      // Load POS transactions for the month
      const monthRef = collection(db, "posUploads", month, "records");
      const q = query(
        monthRef,
        where("date", ">=", startDateStr),
        where("date", "<=", endDateStr)
      );
      const snap = await getDocs(q);
      console.log("Loaded POS records:", snap.size);

      // Group by car
      const carsAgg = {}; // carId -> {carNumber, company, weeks, total}

      for (const doc of snap.docs) {
        const tx = doc.data();
        
        // Filter by company if selected
        if (company && tx.carCompany !== company) continue;
        
        const weekKey = getWeekKeyFromDate(tx.date);
        if (!weekKey || !selectedWeeks.includes(weekKey)) continue;

        const carId = tx.carId;
        if (!carId) continue;

        if (!carsAgg[carId]) {
          carsAgg[carId] = {
            carId,
            carNumber: tx.carNumber || "UNKNOWN",
            company: tx.carCompany || "",
            vehicleType: tx.vehicleType || "",
            weeks: {}, // weekKey -> amount
            total: 0,
          };
        }

        // Add to week total
        if (!carsAgg[carId].weeks[weekKey]) {
          carsAgg[carId].weeks[weekKey] = 0;
        }
        carsAgg[carId].weeks[weekKey] += Number(tx.amount || 0);
        carsAgg[carId].total += Number(tx.amount || 0);
      }

      // Create columns (one per selected week)
      const cols = selectedWeeks.map((w) => ({
        key: w,
        label: w.toUpperCase(),
        weekKey: w,
      }));
      setColumns(cols);

      // Build rows
      const finalRows = Object.values(carsAgg).map((car) => {
        const row = {
          carId: car.carId,
          carNumber: car.carNumber,
          company: car.company,
          vehicleType: car.vehicleType,
          total: car.total,
        };
        selectedWeeks.forEach((w) => {
          row[w] = car.weeks[w] || 0;
        });
        return row;
      });

      // Sort by total descending
      finalRows.sort((a, b) => b.total - a.total);
      setRows(finalRows);

      // Calculate week totals
      const weekTotalsObj = {};
      selectedWeeks.forEach((w) => {
        let sum = 0;
        finalRows.forEach((r) => {
          sum += r[w] || 0;
        });
        weekTotalsObj[w] = sum;
      });
      setWeekTotals(weekTotalsObj);

      // Calculate grand total
      const total = finalRows.reduce((sum, r) => sum + r.total, 0);
      setGrandTotal(total);

    } finally {
      setLoading(false);
    }
  }

  async function downloadExcel() {
    if (!rows.length) {
      alert("No data to export. Please generate report first.");
      return;
    }

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("POS Summary");

    const header = [
      "Car Number",
      "Company",
      "Vehicle Type",
      ...columns.map((c) => c.label),
      "Total",
    ];

    ws.addRow(header);

    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    headerRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      if (colNumber > 3 && colNumber <= 3 + columns.length) {
        const colIndex = colNumber - 4;
        const colMeta = columns[colIndex];
        const fillColor = WEEK_COLORS[colMeta.weekKey] || "#ffffff";
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: cssHexToExcelARGB(fillColor) },
        };
      } else {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEFEFEF" },
        };
      }
    });

    rows.forEach((r) => {
      const rowData = [
        r.carNumber,
        r.company,
        r.vehicleType,
        ...columns.map((c) => Number(r[c.key] || 0)),
        r.total,
      ];
      const excelRow = ws.addRow(rowData);

      excelRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (colNumber > 3) {
          cell.numFmt = "0.00";
        }
      });
    });

    // Total row
    const totalRowValues = [
      "TOTAL",
      "",
      "",
      ...columns.map((c) => weekTotals[c.key] || 0),
      grandTotal,
    ];

    const totalRow = ws.addRow(totalRowValues);
    totalRow.font = { bold: true };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber > 3) {
        cell.numFmt = "0.00";
      }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    });

    ws.columns.forEach((col) => {
      let maxLen = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value ? cell.value.toString() : "";
        if (v.length > maxLen) maxLen = v.length;
      });
      col.width = maxLen + 2;
    });

    const fileName = `pos_summary_${month}_${weekFrom}-${weekTo}${company ? `_${company}` : ''}.xlsx`;
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), fileName);
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h2>POS Transactions — Car-Wise Weekly Summary</h2>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 20,
          marginTop: 10,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <label>Month</label>
          <br />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>

        <div>
          <label>Week From</label>
          <br />
          <select
            value={weekFrom}
            onChange={(e) => setWeekFrom(e.target.value)}
          >
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Week To</label>
          <br />
          <select value={weekTo} onChange={(e) => setWeekTo(e.target.value)}>
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Company (Optional)</label>
          <br />
          <select
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={generateReport}
          disabled={loading}
          style={{
            padding: "10px 16px",
            background: "#0037ff",
            color: "white",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            minWidth: 120,
          }}
        >
          {loading ? "Loading..." : "Generate"}
        </button>

        {rows.length > 0 && (
          <button
            onClick={downloadExcel}
            style={{
              padding: "10px 16px",
              background: "green",
              color: "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
            }}
          >
            Download Excel
          </button>
        )}
      </div>

      {loading && (
        <p style={{ marginTop: 15 }}>⏳ Generating report, please wait…</p>
      )}

      {/* TABLE */}
      {rows.length > 0 && (
        <div style={{ marginTop: 20, overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              border: "1px solid #ccc",
            }}
          >
            <thead>
              <tr style={{ background: "#f3f4ff" }}>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Car Number</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Company</th>
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Vehicle Type</th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      background: WEEK_COLORS[c.weekKey] || "#ffffff",
                    }}
                  >
                    {c.label}
                  </th>
                ))}
                <th style={{ border: "1px solid #ccc", padding: 6 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td style={{ border: "1px solid #ccc", padding: 6, textAlign: "center" }}>{idx + 1}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.carNumber}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.company}</td>
                  <td style={{ border: "1px solid #ccc", padding: 6 }}>{r.vehicleType}</td>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        border: "1px solid #ccc",
                        padding: 6,
                        textAlign: "right",
                        background: WEEK_COLORS[c.weekKey] || "#ffffff",
                      }}
                    >
                      {Number(r[c.key] || 0).toFixed(2)}
                    </td>
                  ))}
                  <td
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    {r.total.toFixed(2)}
                  </td>
                </tr>
              ))}

              {/* TOTAL ROW */}
              <tr style={{ fontWeight: "bold", background: "#eef2ff" }}>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "center",
                  }}
                >
                  TOTAL
                </td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}></td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}></td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      background: WEEK_COLORS[c.weekKey] || "#ffffff",
                    }}
                  >
                    {(weekTotals[c.key] || 0).toFixed(2)}
                  </td>
                ))}
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {grandTotal.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}